<template>
  <div class="h-full">
    <div v-if="loading" class="flex items-center justify-center h-full">
      <loading-spinner diameter="64px" />
    </div>

    <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <div v-else class="flex bg-white mx-4 pb-4">
      <!-- LEFT PANEL - Interactive Fulfillment Table -->
      <div class="cart w-3/4 mt-10 px-6">
        <div class="header h-14 flex flex-1 justify-between gap-2">
          <div class="search-box h-full w-full">
            <BarcodeScanner @scan="handleScan" v-model:is-scanning="isScanning" />
            <el-autocomplete
              v-model="itemSearchTerm"
              :fetch-suggestions="searchItems"
              :placeholder="t('searchItems')"
              @select="handleItemSelect"
              class="w-full h-full"
              value-key="name"
              @keypress.enter.prevent="onSearchSelectPressedEnter"
              @keyup.enter.prevent="onSearchSelectKeyupEnter"
              :trigger-on-focus="false"
              highlight-first-item
              :prefix-icon="Search"
              ref="searchInput"
            >
              <template #default="{ item }">
                <div
                  :class="{ 'flex-row-reverse': $ctx.locale == 'ar' }"
                  class="flex justify-between"
                >
                  <div>{{ item.name }}</div>
                  <div v-if="!item.unlimitedStock">
                    {{ getAvailableQuantity(item) }}
                  </div>
                </div>
              </template>
            </el-autocomplete>
          </div>
          <div>
            <el-switch
              v-model="appendToFulfillmentItems"
              inline-prompt
              active-text="+"
              inactive-text="="
            />
          </div>
        </div>

        <div class="items-table w-full mt-6 overflow-y-scroll flex flex-col">
          <el-table
            :table-layout="'fixed'"
            :data="fulfillmentItems"
            stripe
            border
            style="width: 100%"
            height="100%"
            class="card rounded"
            header-cell-class-name="my-header"
            :row-class-name="getTableRowClass"
            ref="fulfillmentTable"
          >
            <el-table-column type="selection" width="55" :selectable="() => true" />

            <el-table-column :min-width="90" prop="item.externalID" :label="t('itemCode')">
              <template #default="scope">
                <div class="flex items-center justify-between">
                  <span>{{
                    (scope.row.packInfo?.itemPack?.barcode) ||
                    scope.row.item.barcode ||
                    scope.row.item.externalID
                  }}</span>
                </div>
              </template>
            </el-table-column>

            <el-table-column :min-width="150" :label="t('itemName')">
              <template #default="scope">
                <div class="flex items-center justify-between">
                  <div class="truncate">
                    <span>{{ scope.row.item.name }}</span>
                  </div>
                  <div style="width: 30px">
                    <span v-if="scope.row.item.isSerialized" @click="openSerialDialog(scope.row)">
                      <img src="../../assets/serials.png" alt="serials icon" class="item-serial" />
                    </span>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column :min-width="150" :label="t('batchNumber')">
              <template #default="scope">
                <el-select
                  v-if="scope.row.item.isExpirable"
                  v-model="scope.row.extraProperties.batchNumber"
                  :placeholder="t('batchNumber')"
                  @change="onBatchNumberChange(scope.row)"
                >
                  <template
                    v-for="stock in scope.row.item.itemStocks"
                    :key="stock.extraProperties?.batchNumber"
                  >
                    <el-option
                      v-if="stock.extraProperties?.batchNumber"
                      :label="stock.extraProperties?.batchNumber"
                      :value="stock.extraProperties?.batchNumber"
                    />
                  </template>
                </el-select>
              </template>
            </el-table-column>

            <el-table-column type="index" :min-width="145" prop="quantity" :label="t('quantity')">
              <template #default="scope">
                <div class="flex flex-col gap-1">
                  <div class="flex items-center gap-2">
                    <QuantityInput
                      :item="scope.row.item"
                      :packInfo="scope.row.packInfo"
                      type="el-input-number"
                      inputClass="checkout-quantity-el-input-number"
                      :class="{ 'overfulfilled-input': isItemOverfulfilled(scope.row) }"
                      :usePackQuantity="true"
                      @onPack="
                        (packInfo) => {
                          onPackFulfillment(packInfo, scope.row)
                        }
                      "
                      v-model="scope.row.quantity"
                      @change="onFulfillmentQuantityChange(scope.row, scope.row.quantity)"
                    >
                        <template #label>
                          {{ getPackDisplayName(scope.row.packInfo) }}
                        </template>
                        <template #qtyLabel>         
                            <div v-if="scope.row.packInfo" class="pack-info-mini !text-blue-600 flex justify-center" style="font-size: 14px;">
                              {{ scope.row.quantity * scope.row.packInfo.packQuantity }} {{ t('units') }}
                            </div>
                            <div v-if="isItemOverfulfilled(scope.row)" class="overfulfillment-note">
                              <span class="text-xs text-red-600 font-medium">
                                ⚠️ {{ getOverfulfillmentMessage(scope.row) }}
                              </span>
                            </div>
                        </template>
                    </QuantityInput>
                    <div v-if="isItemOverfulfilled(scope.row)" class="overfulfilled-warning">
                      <font-awesome-icon icon="exclamation-triangle" class="text-red-500" />
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column :min-width="80" :label="t('delete')" align="center">
              <template #default="scope">
                <span
                  class="text-red-600 cursor-pointer"
                  @click="removeFulfillmentItem(scope.$index)"
                >
                  <font-awesome-icon icon="trash" />
                </span>
              </template>
            </el-table-column>
          </el-table>
          <!-- Fulfillment Summary - Horizontal -->
          <div class="fulfillment-summary-horizontal mt-4 p-4 bg-gray-50 rounded-lg border">
            <div class="flex items-center justify-between">
              <!-- Progress Stats -->
              <div class="progress-stats-horizontal flex items-center gap-8">
                <div class="stat-item-horizontal text-center">
                  <div class="stat-value-horizontal text-xl font-bold text-primary">
                    {{ getTotalOrderedQuantity() }}
                  </div>
                  <div class="stat-label-horizontal text-xs text-gray-600 uppercase tracking-wide">
                    {{ t('totalItems') }}
                  </div>
                </div>
                
                <div class="stat-item-horizontal text-center">
                  <div
                    class="stat-value-horizontal text-xl font-bold"
                    :class="getTotalScannedQuantity() > 0 ? 'text-green-600' : 'text-gray-400'"
                  >
                    {{ getTotalScannedQuantity() }}
                  </div>
                  <div class="stat-label-horizontal text-xs text-gray-600 uppercase tracking-wide">
                    {{ t('totalScanned') }}
                  </div>
                </div>
              </div>
              
              <!-- Progress Bar Section -->
              <div class="progress-section-horizontal flex-1 mx-8">
                <div class="flex justify-between items-center mb-2">
                  <span class="text-sm font-medium text-gray-700">{{
                    t('fulfillmentProgress')
                  }}</span>
                  <span class="text-sm font-bold text-primary">{{ getFulfillmentProgress() }}%</span>
                </div>
                <div class="progress-bar-bg bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div 
                    class="progress-bar-fill bg-gradient-to-r from-blue-500 to-blue-600 h-full transition-all duration-300"
                    :style="{ width: getFulfillmentProgress() + '%' }"
                  ></div>
                </div>
              </div>
              
              <!-- Status -->
              <div class="status-section-horizontal text-right">
                <div class="text-xs font-medium" :class="getProgressStatusClass()">
                  {{ getProgressStatusText() }}
                </div>
              </div>
            </div>
          </div>

          <!-- Order Notes -->
          <div v-if="order.invoice.notes" class="order-notes mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div class="flex items-center mb-2">
              <font-awesome-icon icon="sticky-note" class="text-blue-600 mr-2" />
              <h4 class="text-sm font-bold">{{ t('orderNotes') }}</h4>
            </div>
            <p class="text-sm">{{ order.invoice.notes }}</p>
          </div>
        </div>
      </div>

      <div class="aside w-1/4 border-l-2 flex flex-col mt-10 px-4">
        <!-- Order Header -->
        <div class="order-header mb-4">
          <h2 class="text-xl font-bold text-primary">{{ t('orderDetails') }}</h2>

          <!-- Items Summary -->
          <div class="items-summary mb-4">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-lg font-bold text-gray-800">{{ t('itemsToFulfill') }}</h3>
              <div class="items-count">
                <span class="bg-primary text-white px-2 py-1 rounded-full text-xs font-bold">
                  {{ order.orderItems?.length || 0 }}
                </span>
              </div>
            </div>

            <!-- Items Quick List -->
            <div class="items-quick-list bg-white border rounded-lg p-3">
              <div v-if="order.orderItems?.length" class="space-y-2">
                <div
                  v-for="(orderItem, index) in order.orderItems"
                  :key="orderItem.id || index"
                  class="item-summary-row flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0"
                >
                                   <div class="item-info flex-1 mr-2">
                   <div class="text-base font-semibold text-blue-700 truncate mb-1">
                     {{ orderItem.item.name }}
                     <!-- Pack Name -->
                     <span v-if="isPackItem(orderItem)" class="ml-2 text-blue-600 font-medium">
                       - {{ getPackDisplayNameFromOrderItem(orderItem) }}
                     </span>
                     <!-- Order Item Line Number -->
                     <span class="ml-2 text-xs text-gray-400">
                       (#{{ index + 1 }})
                     </span>
                     
                     <!-- Serial Requirement Indicators -->
                     <div class="inline-flex items-center gap-1 ml-2">
                       <!-- Batch Required Indicator -->
                       <span 
                         v-if="orderItem.item.isExpirable" 
                         class="px-1 py-0.5 text-xs rounded"
                         :class="needsBatchSelection(orderItem) ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' : 'bg-green-100 text-green-800'"
                         :title="needsBatchSelection(orderItem) ? t('batchRequired', {itemName: orderItem.item.name}) : t('batchSelected')"
                       >
                         B
                       </span>
                       
                       <!-- Serial Required Indicator -->
                       <span 
                         v-if="orderItem.item.isSerialized" 
                         class="px-1 py-0.5 text-xs rounded"
                         :class="needsSerialsSelection(orderItem) ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-green-100 text-green-800'"
                         :title="orderItem.item.isSerialized ? `${getSerialSelectionStatus(orderItem).selected}/${getSerialSelectionStatus(orderItem).required} serials` : ''"
                       >
                         S
                       </span>
                     </div>
                   </div>
                   
                   <div class="flex items-center gap-2 text-sm font-medium">
                     <!-- Pack-aware quantity display -->
                     <span v-if="isPackItem(orderItem)">
                       {{ t('quantity') }}: <span class="text-blue-600">{{ orderItem.quantity }} {{ getPackDisplayNameFromOrderItem(orderItem) }} ({{ getDisplayQuantity(orderItem) }} {{ t('units') }})</span>
                       
                     </span>
                     <span v-else>
                       {{ t('quantity') }}: <span class="text-blue-600">{{ orderItem.quantity }}</span>
                     </span>
                   </div>

                   <!-- Order Item Notes -->
                   <div v-if="orderItem.notes" class="text-sm text-blue-600 mt-1">
                     {{ orderItem.notes }}
                   </div>
                 </div>
                  <div class="fulfillment-status">
                    <div
                      class="fulfillment-indicator"
                      :class="getFulfillmentIndicatorClass(orderItem)"
                    >
                      <template v-if="getFulfilledQuantity(orderItem) > getRequiredQuantity(orderItem)">
                        {{ getRequiredQuantity(orderItem) }}/{{ getRequiredQuantity(orderItem) }}
                        <span class="excess-indicator">+{{ getFulfilledQuantity(orderItem) - getRequiredQuantity(orderItem) }}</span>
                      </template>
                      <template v-else>
                        {{ getFulfilledQuantity(orderItem) }}/{{ getRequiredQuantity(orderItem) }}
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-4 text-gray-500 text-sm">
                {{ t('noOrderItems') }}
              </div>
            </div>
          </div>

          <!-- Order Info Card -->
          <div class="order-info-card bg-light-blue p-4 rounded-lg border">
            <div class="order-id-section mb-3">
              <div class="text-xs font-medium text-gray-600 uppercase tracking-wide">
                {{ t('orderNumber') }}
              </div>
              <div class="text-lg font-bold text-primary mt-1">
                #{{ order.externalID || order.id }}
              </div>
            </div>

            <div class="customer-section mb-3">
              <div class="text-xs font-medium text-gray-600 uppercase tracking-wide">
                {{ t('customer') }}
              </div>
              <div
                class="text-sm font-medium mt-1"
                :class="order.invoice?.customer?.name ? 'text-gray-800' : 'text-gray-500'"
              >
                {{ order.invoice?.customer?.name || t('noCustomer') }}
              </div>
            </div>

            <div class="flex justify-between items-center">
              <div class="type-section">
                <div class="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  {{ t('orderType') }}
                </div>
                <div class="text-sm font-medium mt-1 text-gray-800">
                  {{ t(`orderTypes.${order.orderType}`) }}
                </div>
              </div>

              <div class="status-section">
                <div class="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  {{ t('status') }}
                </div>
                <div class="status-badge mt-1" :class="getStatusBadgeClass()">
                  {{ order.orderStatus }}
                </div>
              </div>
            </div>
          </div>
        </div>

        

        <!-- Overfulfillment Warning -->
        <div v-if="hasOverfulfillment()" class="overfulfillment-warning mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div class="flex items-center mb-2">
            <font-awesome-icon icon="exclamation-triangle" class="text-red-600 mr-2" />
            <h4 class="text-sm font-bold text-red-800">{{ t('overfulfillmentError') }}</h4>
          </div>
          <p class="text-xs text-red-700 mb-2">{{ t('overfulfillmentWarning') }}</p>
          <div class="overfulfillment-details text-xs">
            <div
              v-for="item in getOverfulfillmentDetails()"
              :key="item.itemName"
              class="text-red-600 font-medium"
            >
              {{ item.itemName }}: {{ item.fulfilled }}/{{ item.required }} (+{{ item.excess }} excess)
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-section">
          <div class="action-buttons space-y-3">
            <horizontal-button
              :title="t('buttons.completeFulfillment')"
              styles="button-primary 2xl:h-20 lg:h-16 h-12 2xl:text-2xl lg:text-xl text-base font-bold"
              :rounded="false"
              @click="openCashierPinDialog"
              :disabled="!canCompleteFulfillment"
            />
            <horizontal-button
              v-if="fulfillmentItems.length"
              :title="t('buttons.cancel')"
              faIcon="cancel"
              styles="border border-red-500 h-12 text-l text-red-500 hover:bg-red-500 hover:text-white"
              @click="cancelFulfillment"
              :rounded="false"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Serial Selection Dialog -->
  <el-dialog :title="t('selectSerials')" v-model="serialDialogVisible" width="30%">
    <div v-if="currentFulfillmentItem">
      <p class="mb-4">{{ t('selectSerialsFor') }} {{ currentFulfillmentItem.item.name }}</p>

      <!-- Show batch info for expirable items -->
      <div
        v-if="currentFulfillmentItem.item.isExpirable && currentFulfillmentItem.extraProperties?.batchNumber"
        class="mb-3 p-2 bg-blue-50 rounded"
      >
        <span class="text-sm text-blue-700">
          {{ t('batch') }}: {{ currentFulfillmentItem.extraProperties.batchNumber }}
        </span>
      </div>

      <!-- Show warning if batch not selected for expirable items -->
      <div
        v-else-if="currentFulfillmentItem.item.isExpirable && !currentFulfillmentItem.extraProperties?.batchNumber"
        class="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded"
      >
        <span class="text-sm text-yellow-700">
          {{ t('selectBatchFirst') }}
        </span>
      </div>

      <!-- Serial selection -->
      <el-select
        v-model="currentFulfillmentItem.serialIDs"
        multiple
        filterable
        :placeholder="t('selectSerials')"
        class="w-full"
        :multiple-limit="currentFulfillmentItem.quantity"
        :disabled="currentFulfillmentItem.item.isExpirable && !currentFulfillmentItem.extraProperties?.batchNumber"
      >
        <el-option
          v-for="serial in getAvailableSerials(currentFulfillmentItem)"
          :key="serial.id"
          :label="serial.number"
          :value="serial.id"
        />
      </el-select>

      <!-- Show required serials count -->
      <div class="mt-2 text-sm text-gray-600">
        {{ t('serialsSelected') }}: {{ (currentFulfillmentItem.serialIDs || []).length }}
      </div>
    </div>
    <template #footer>
      <el-button @click="serialDialogVisible = false">{{ t('cancel') }}</el-button>
      <el-button type="primary" @click="serialDialogVisible = false">{{ t('confirm') }}</el-button>
    </template>
  </el-dialog>

  <!-- Cashier PIN Dialog -->
  <el-dialog v-model="cashierPinDialogOpen" width="30%" :title="t('enterCashierPin')">
    <div>
      <div class="my-2">
        <p>{{ t('enterCashierPin') }}</p>
      </div>
      <div class="my-2">
        <el-input
          id="password"
          v-model="cashierPin"
          type="password"
          required
          show-password
          autofocus
          dir="ltr"
          :maxlength="4"
          :minlength="4"
          @keyup.enter="submitFulfillment"
          ref="cashierPinInput"
        />
      </div>
      <div class="flex items-center justify-between mb-2">
        <el-button type="primary" @click="submitFulfillment" class="w-full">
          {{ t('completeFulfillment') }}
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { useContext } from '@/plugins/context'
import { useRouter, useRoute } from 'vue-router'
import { useSettingsStore } from '@/stores/settingsStore'

// Models
import { Order, OrderItem, i18nOrderMessages } from '@/models/sales/Order'
import Item from '@/models/inventory/Item'
import Pack from '@/models/inventory/Pack'

// Components
import AlertBadge from '@/components/AlertBadge.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import BarcodeScanner from './BarcodeScanner.vue'
import QuantityInput from '@/views/inventory/items/QuantityInput.vue'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Dinero, { Currency } from 'dinero.js'
import { formatDinero } from '@/utils/money'
import successbeep from '@/assets/sounds/success.mp3'
import errorbeep from '@/assets/sounds/error.mp3'

export default defineComponent({
  name: 'OrderFulfillmentTwoPanel',

  components: {
    AlertBadge,
    LoadingSpinner,
    HorizontalButton,
    BarcodeScanner,
    QuantityInput,
    FontAwesomeIcon
  },

  setup() {
    const { t } = useI18n({
      useScope: 'global',
      messages: {
        en: {
          ...i18nOrderMessages.en,
          orderDetails: 'Order Details',
          itemsToFulfill: 'Items to Fulfill',
          scannedItems: 'Scanned Items',
          searchItems: 'Enter item barcode or SKU or name',
          totalItems: 'Total Items',
          totalScanned: 'Total Scanned',
          fulfillmentProgress: 'Fulfillment Progress',
          fulfillmentSummary: 'Fulfillment Summary',
          readyToFulfill: 'Ready to fulfill',
          scanItemsToStart: 'Scan items to start fulfillment',
          noOrderItems: 'No items in this order',
          notStarted: 'Not Started',
          inProgress: 'In Progress',
          completed: 'Completed',
          serial: 'Serial',
          single: 'Single',
          total: 'total',
          units: 'units',
          createdAt: 'Created',
          unknown: 'Unknown',
          itemCode: 'Item Code',
          itemName: 'Item Name',
          quantity: 'Quantity',
          packInfo: 'Pack',
          added: 'Added',
          serialNumbers: 'Serial Numbers',
          batchNumber: 'Batch Number',
          noBatch: 'No batch',
          selectBatchFirst: 'Select batch first',
          selectSerials: 'Select {num} serial numbers',
          noSerials: 'No serials required',
          delete: 'Delete',
          status: 'Status',
          pending: 'Pending',
          partial: 'Partial',
          complete: 'Complete',
          orderNumber: 'Order #',
          customer: 'Customer',
          orderType: 'Order Type',
          noCustomer: 'No Customer',
          enterCashierPin: 'Enter Cashier PIN',
          errCashierPinRequired: 'Cashier PIN is required',
          completeFulfillment: 'Complete Fulfillment',
          itemNotFound: 'Item Not Found',
          itemNotInOrder: 'Item {name} is not in this order',
          itemAlreadyFulfilled: 'Item {name} is already fully fulfilled',
          fulfillmentCompleteSuccess: 'Fulfillment completed successfully!',
          orderTypes: {
            direct: 'Direct',
            delivery: 'Delivery',
            pick_up: 'Pickup'
          },
          buttons: {
            completeFulfillment: 'Complete Fulfillment',
            cancel: 'Cancel'
          },
          notes: 'Notes',
          overfulfillmentError: 'Overfulfillment Error',
          overfulfillmentWarning: 'Some items are overfulfilled. Please adjust quantities before completing.',
          cannotSubmitOverfulfilled: 'Cannot complete fulfillment with overfulfilled items',
          reduceBy: 'Reduce by',
          selectSerials: 'Select Serials',
          selectSerialsFor: 'Select serials for',
          batch: 'Batch',
          serialsRequired: 'Serials Required',
          serialsSelected: 'Serials Selected',
          batchChangedResetSerials: 'Batch changed - please reselect serials',
          fulfillmentIncomplete: 'Please complete all required fields before submitting',
          batchRequired: 'Batch number required for {itemName}',
          batchSelected: 'Batch number selected',
          itemNotFullyFulfilled: '{itemName}: {remaining} more needed (fulfilled {fulfilled}/{required})',
          orderNotes: 'Order Notes'
        },
        ar: {
          ...i18nOrderMessages.ar,
          orderDetails: 'تفاصيل الطلب',
          itemsToFulfill: 'المواد المطلوب تجهيزها',
          scannedItems: 'المواد المجهزة',
          searchItems: 'ادخل الباركود او SKU او اسم المنتج',
          totalItems: 'إجمالي المواد',
          totalScanned: 'إجمالي المجهز',
          fulfillmentProgress: 'نسبة التقدم',
          fulfillmentSummary: 'ملخص الإنجاز',
          readyToFulfill: 'جاهز للإنجاز',
          scanItemsToStart: 'امسح المواد لبدء التجهيز',
          noOrderItems: 'لا توجد عناصر في هذا الطلب',
          notStarted: 'لم يبدأ',
          inProgress: 'قيد التحضير',
          completed: 'مكتمل',
          serial: 'مسلسل',
          single: 'واحد',
          total: 'إجمالي',
          units: 'قطع',
          createdAt: 'تاريخ الإنشاء',
          unknown: 'غير معروف',
          itemCode: 'كود المنتج',
          itemName: 'اسم المنتج',
          quantity: 'الكمية',
          packInfo: 'العبوة',
          added: 'مضاف',
          serialNumbers: 'الأرقام التسلسلية',
          batchNumber: 'رقم الوجبة',
          noBatch: 'بدون دفعة',
          selectBatchFirst: 'اختر الوجبة أولاً',
          selectSerials: 'اختر {num} أرقام تسلسلية',
          noSerials: 'لا يتطلب أرقام تسلسلية',
          delete: 'حذف',
          status: 'الحالة',
          pending: 'معلق',
          partial: 'جزئي',
          complete: 'مكتمل',
          orderNumber: 'رقم الطلب',
          customer: 'الزبون',
          orderType: 'نوع الطلب',
          noCustomer: 'لا يوجد عميل',
          enterCashierPin: 'أدخل رمز الكاشير',
          errCashierPinRequired: 'رمز الكاشير مطلوب',
          completeFulfillment: 'إتمام التجهيز',
          itemNotFound: 'المنتج غير موجود',
          itemNotInOrder: 'المنتج {name} ليس في هذا الطلب',
          itemAlreadyFulfilled: 'المنتج {name} تم إنجازه بالكامل',
          fulfillmentCompleteSuccess: 'تم إكمال التجهيز بنجاح!',
          orderTypes: {
            direct: 'مباشر',
            delivery: 'توصيل',
            pick_up: 'استلام'
          },
          buttons: {
            completeFulfillment: 'إتمام التجهيز',
            cancel: 'إلغاء'
          },
          notes: 'ملاحظات',
          overfulfillmentError: 'الكمية غير صحيحة',
          overfulfillmentWarning: 'تم اضافة كميات لبعض المنتجات اكثر من الكميات المطلوبة',
          cannotSubmitOverfulfilled: 'لا يمكن إكمال التجهيز مع وجود منتجات مفرطة التجهيز',
          reduceBy: 'قلل بـ',
          selectSerials: 'اختر الأرقام التسلسلية',
          selectSerialsFor: 'اختر الأرقام التسلسلية لـ',
          batch: 'مخزون',
          serialsRequired: 'الأرقام التسلسلية المطلوبة',
          serialsSelected: 'الأرقام التسلسلية المختارة',
          confirm: 'تأكيد',
          batchChangedResetSerials: 'تم تغيير المخزون - يرجى إعادة اختيار الأرقام التسلسلية',
          fulfillmentIncomplete: 'يرجى إكمال جميع الحقول المطلوبة قبل الإرسال',
          batchRequired: 'رقم الوجبة مطلوب لـ {itemName}',
          batchSelected: 'تم اختيار رقم الوجبة',
          itemNotFullyFulfilled: '{itemName}: {remaining} مطلوب أكثر (تم إنجاز {fulfilled}/{required})',
          orderNotes: 'ملاحظات الطلب'
        }
      }
    })

    const http = useHTTP()
    const alertModal = useAlertModal()
    const ctx = useContext()
    const router = useRouter()
    const route = useRoute()
    const settingsStore = useSettingsStore()

    return { t, http, alertModal, ctx, router, route, settingsStore, Search }
  },

  data() {
    return {
      loading: false,
      error: { title: '', body: '' },
      order: new Order(),
      fulfillmentItems: [] as OrderItem[],
      itemSearchTerm: '',
      appendToFulfillmentItems: false,
      pressedEnter: false,
      isScanning: false,
      cashierPinDialogOpen: false,
      cashierPin: '',
      packs: [] as Pack[],
      packMap: new Map<string, Pack>(),
      serialDialogVisible: false,
      currentFulfillmentItem: null as OrderItem | null,
      currentItemPack: null as any
    }
  },

  computed: {
    canCompleteFulfillment(): boolean {
      if (this.fulfillmentItems.length === 0) return false
      if (this.hasOverfulfillment()) return false
      return this.validateFulfillmentItems().length === 0
    }
  },

  async mounted() {
    await Promise.all([
      this.loadOrder(),
      this.loadPacks()
    ])
  },

  methods: {
    async loadOrder() {
      try {
        this.loading = true
        const orderId = this.$route.params.id as string
        const response = await this.$http.get<Order>(`${Order.ENDPOINT}/${orderId}`)
        this.order = Order.from(response)
      } catch (error: any) {
        this.error.title = error.title || 'Error'
        this.error.body = error.body || 'Failed to load order'
      } finally {
        this.loading = false
      }
    },

    async loadPacks() {
      try {
        const response = await this.$http.get<Pack[]>(Pack.ENDPOINT)
        this.packs = response.map(pack => Pack.from(pack))
        
        // Create a map for quick lookup
        this.packMap.clear()
        this.packs.forEach(pack => {
          if (pack.id) {
            this.packMap.set(pack.id, pack)
          }
        })
      } catch (error: any) {
        console.error('Failed to load packs:', error)
        // Don't show error to user, just log it
      }
    },

    async searchItems(query: string, cb: Function) {
      if (!query) {
        cb([])
        return
      }

      try {
        const url = `${Item.ENDPOINT}?type=checkout&q=${query}`
        const items = await this.$http.get<Item[]>(url)
        const itemsFormatted = items.map((item) => Item.from(item))

        // Check if this query matches a pack barcode/SKU
        let itemPack = null
        if (itemsFormatted.length && Array.isArray(itemsFormatted[0].itemPacks)) {
          for (const ip of itemsFormatted[0].itemPacks) {
            if (ip.barcode === query || ip.sku === query) {
              itemPack = ip
              break
            }
          }
        }

        // Store the pack info for later use
        if (itemPack) {
          this.currentItemPack = itemPack
        } else {
          this.currentItemPack = null
        }

        if (this.pressedEnter && itemsFormatted.length === 1) {
          // Play success beep
          if (this.settingsStore.getValue('scan_beep') == 'true') {
            const successAudio = new Audio(successbeep)
            successAudio.play()
          }

          if (itemPack) {
            this.addFulfillmentItemWithPack(itemsFormatted[0], itemPack)
          } else {
            this.addFulfillmentItem(itemsFormatted[0])
          }
          cb([])
        } else if (this.pressedEnter && itemsFormatted.length === 0) {
          ElMessage.error(this.t('itemNotFound'))
          this.itemSearchTerm = '' // Clear search input after error
          // Play error beep
          if (this.settingsStore.getValue('scan_error_beep') == 'true') {
            const errorAudio = new Audio(errorbeep)
            errorAudio.play()
          }
          cb([])
        } else {
          cb(itemsFormatted)
        }
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title })
        this.itemSearchTerm = '' // Clear search input after error
        // Play error beep
        if (this.settingsStore.getValue('scan_error_beep') == 'true') {
          const errorAudio = new Audio(errorbeep)
          errorAudio.play()
        }
        cb([])
      }

      this.pressedEnter = false
    },

    handleItemSelect(selectedItem: Item) {
      const tmpItemPack = this.currentItemPack
      this.currentItemPack = null

      if (tmpItemPack) {
        this.addFulfillmentItemWithPack(selectedItem, tmpItemPack)
      } else {
        this.addFulfillmentItem(selectedItem)
      }
    },

    async handleScan(barcode: string) {
      if (!barcode || barcode.trim() === '') {
        return
      }

      try {
        const url = `${Item.ENDPOINT}?type=checkout&q=${barcode}`
        const items = await this.$http.get<Item[]>(url)
        const itemsFormatted = items.map((item) => Item.from(item))

        if (itemsFormatted.length === 1) {
          // Check if this barcode matches a pack barcode/SKU
          let itemPack = null
          if (itemsFormatted[0].itemPacks && Array.isArray(itemsFormatted[0].itemPacks)) {
            for (const ip of itemsFormatted[0].itemPacks) {
              if (ip.barcode === barcode || ip.sku === barcode) {
                itemPack = ip
                break
              }
            }
          }

          // Play success beep
          if (this.settingsStore.getValue('scan_beep') == 'true') {
            const successAudio = new Audio(successbeep)
            successAudio.play()
          }

          if (itemPack) {
            // Add the item with pack information
            this.addFulfillmentItemWithPack(itemsFormatted[0], itemPack)
          } else {
            // Add as individual item
            this.addFulfillmentItem(itemsFormatted[0])
          }
        } else if (itemsFormatted.length === 0) {
          ElMessage.error(this.t('itemNotFound'))
          this.itemSearchTerm = '' // Clear search input after error
          // Play error beep
          if (this.settingsStore.getValue('scan_error_beep') == 'true') {
            const errorAudio = new Audio(errorbeep)
            errorAudio.play()
          }
        } else {
          // Multiple items found - add the first one
          // Check if this barcode matches a pack barcode/SKU for the first item
          let itemPack = null
          if (itemsFormatted[0].itemPacks && Array.isArray(itemsFormatted[0].itemPacks)) {
            for (const ip of itemsFormatted[0].itemPacks) {
              if (ip.barcode === barcode || ip.sku === barcode) {
                itemPack = ip
                break
              }
            }
          }

          // Play success beep
          if (this.settingsStore.getValue('scan_beep') == 'true') {
            const successAudio = new Audio(successbeep)
            successAudio.play()
          }

          if (itemPack) {
            this.addFulfillmentItemWithPack(itemsFormatted[0], itemPack)
          } else {
            this.addFulfillmentItem(itemsFormatted[0])
          }
        }
      } catch (error) {
        ElMessage.error(this.t('itemNotFound'))
        this.itemSearchTerm = '' // Clear search input after error
        // Play error beep
        if (this.settingsStore.getValue('scan_error_beep') == 'true') {
          const errorAudio = new Audio(errorbeep)
          errorAudio.play()
        }
      }
    },

    addFulfillmentItemWithPack(selectedItem: Item, itemPack: any, packCountToAdd: number = 1) {
      this.itemSearchTerm = ''

      // Find all order items with this item
      const orderItems = this.order.orderItems.filter((oi) => oi.item.id === selectedItem.id)
      if (orderItems.length === 0) {
        ElMessage.warning(this.t('itemNotInOrder', { name: selectedItem.name }))
        this.itemSearchTerm = '' // Clear search input after error
        return
      }

      // For pack scanning, calculate total units (packs * units per pack)
      const packQuantity = itemPack.qty || 1  // Units per pack
      const totalUnitsToAdd = packCountToAdd * packQuantity

      // Check if we can accept any more of this item
      const totalRequired = orderItems.reduce((sum, oi) => sum + this.getRequiredQuantity(oi), 0)
      const totalCurrentlyFulfilled = this.getTotalFulfilledForItem(selectedItem.id)
      const totalCanAccept = Math.max(0, totalRequired - totalCurrentlyFulfilled)

      if (totalCanAccept === 0) {
        ElMessage.warning(this.t('itemAlreadyFulfilled', { name: selectedItem.name }))
        this.itemSearchTerm = '' // Clear search input after error
        return
      }

      // Limit the quantity to add based on what we can actually accept
      const actualUnitsToAdd = Math.min(totalUnitsToAdd, totalCanAccept)
      const actualPacksToAdd = Math.ceil(actualUnitsToAdd / packQuantity)
      const excessUnits = totalUnitsToAdd - actualUnitsToAdd

      // Create pack info structure matching Order.addItemPack
      const packInfo = {
        packID: itemPack.packID,
        packQuantity: packQuantity,
        soldQuantity: actualPacksToAdd,
        itemPackID: itemPack.id,
        itemPack: itemPack,
        salePriceAmount: itemPack.salePriceAmount
      }

      // Add to fulfillment pool as a pack item
      let existingFulfillmentItem = this.fulfillmentItems.find(fi =>
        fi.item.id === selectedItem.id && fi.packInfo?.packID === packInfo.packID
      )

      if (existingFulfillmentItem && !this.appendToFulfillmentItems) {
        // Increase the pack quantity (soldQuantity)
        existingFulfillmentItem.packInfo.soldQuantity += actualPacksToAdd
        existingFulfillmentItem.quantity = existingFulfillmentItem.packInfo.soldQuantity  // Just the number of packs
      } else {
        const newFulfillmentItem = new OrderItem(selectedItem)
        newFulfillmentItem.quantity = actualPacksToAdd  // Quantity should be number of packs, not units
        newFulfillmentItem.packInfo = packInfo
        newFulfillmentItem.extraProperties = { batchNumber: '' }
        newFulfillmentItem.serialIDs = []
        // Set pack pricing
        newFulfillmentItem.itemSalePriceAmount = itemPack.salePriceAmount
        this.fulfillmentItems.push(newFulfillmentItem)
      }

      // Show feedback
      const packName = this.getPackDisplayName(packInfo)
      const excessLabel = this.$ctx.locale == 'en' ? 'excess': 'زيادة';
      if (excessUnits > 0) {
        ElMessage.info(`${selectedItem.name}: Added ${actualPacksToAdd} ${packName} (${actualUnitsToAdd} units), ${excessLabel} ${excessUnits} units ignored`)
      } else {
        ElMessage.success(`${selectedItem.name}: Added ${actualPacksToAdd} ${packName} (${actualUnitsToAdd} units)`)
      }

      // Log current distribution status
      if (selectedItem.id) {
        this.logDistributionStatus(selectedItem)
      }
    },

    addFulfillmentItem(selectedItem: Item, quantityToAdd: number = 1) {
      this.itemSearchTerm = ''

      // Find all order items with this item
      const orderItems = this.order.orderItems.filter((oi) => oi.item.id === selectedItem.id)
      if (orderItems.length === 0) {
        ElMessage.warning(this.t('itemNotInOrder', { name: selectedItem.name }))
        this.itemSearchTerm = '' // Clear search input after error
        return
      }

      // Check if we can accept any more of this item
      const totalRequired = orderItems.reduce((sum, oi) => sum + this.getRequiredQuantity(oi), 0)
      const totalCurrentlyFulfilled = this.getTotalFulfilledForItem(selectedItem.id)
      const totalCanAccept = Math.max(0, totalRequired - totalCurrentlyFulfilled)

      if (totalCanAccept === 0) {
        ElMessage.warning(this.t('itemAlreadyFulfilled', { name: selectedItem.name }))
        this.itemSearchTerm = '' // Clear search input after error
        return
      }

      // Limit the quantity to add based on what we can actually accept
      const actualQuantityToAdd = Math.min(quantityToAdd, totalCanAccept)
      const excessQuantity = quantityToAdd - actualQuantityToAdd

      // Add to fulfillment pool (simplified approach)
      // Only find items without pack info (to avoid mixing individual items with packs)
      let existingFulfillmentItem = this.fulfillmentItems.find(fi => fi.item.id === selectedItem.id && !fi.packInfo)

      if (existingFulfillmentItem && !this.appendToFulfillmentItems) {
        existingFulfillmentItem.quantity += actualQuantityToAdd
      } else {
        const newFulfillmentItem = new OrderItem(selectedItem)
        newFulfillmentItem.quantity = actualQuantityToAdd
        newFulfillmentItem.extraProperties = { batchNumber: '' }
        newFulfillmentItem.serialIDs = []
        this.fulfillmentItems.push(newFulfillmentItem)
      }

      // Show feedback
      if (excessQuantity > 0) {
        ElMessage.info(`${selectedItem.name}: Added ${actualQuantityToAdd}, excess ${excessQuantity} ignored (all order items would be overfulfilled)`)
      } else {
        ElMessage.success(`${selectedItem.name}: Added ${actualQuantityToAdd}`)
      }

      // Log current distribution status
      if (selectedItem.id) {
        this.logDistributionStatus(selectedItem)
      }
      this.appendToFulfillmentItems = false
    },

    // Helper method for bulk fulfillment (could be used by future UI components)
    addFulfillmentItemsInBulk(selectedItem: Item, totalQuantity: number) {
      if (totalQuantity <= 0) return
      
      ElMessage.info(`Adding ${totalQuantity} of ${selectedItem.name}...`)
      this.addFulfillmentItem(selectedItem, totalQuantity)
    },

    // Debug method to check order item fulfillment status
    debugOrderItemStatus(itemName: string) {
      const orderItems = this.order.orderItems.filter((oi) => oi.item.name.includes(itemName))

      orderItems.forEach((orderItem, index) => {
        const fulfilled = this.getFulfilledQuantity(orderItem)
        const required = this.getRequiredQuantity(orderItem)
      })
    },

    // Test method to simulate the scenario described by the user
    getTotalFulfilledForItem(itemId: string | undefined): number {
      if (!itemId) return 0
      return this.fulfillmentItems
        .filter(fi => fi.item.id === itemId)
        .reduce((sum, fi) => {
          // For pack items: quantity * pack quantity (total individual units)
          // For single items: just the quantity
          if (fi.packInfo?.packQuantity) {
            return sum + (fi.quantity * fi.packInfo.packQuantity)
          }
          return sum + fi.quantity
        }, 0)
    },

    logDistributionStatus(item: Item) {

      if (!item.id) {
        return
      }

      const orderItems = this.order.orderItems.filter(oi => oi.item.id === item.id)
      const totalFulfilled = this.getTotalFulfilledForItem(item.id)
      const totalRequired = orderItems.reduce((sum, oi) => sum + this.getRequiredQuantity(oi), 0)


      orderItems.forEach((oi, idx) => {
        const fulfilled = this.getFulfilledQuantity(oi)
        const required = this.getRequiredQuantity(oi)
      })
    },

    hasOverfulfillment(): boolean {
      // Check if any item has more fulfilled than required
      const uniqueItems = new Map<string, boolean>()

      for (const orderItem of this.order.orderItems) {
        if (!orderItem.item.id || uniqueItems.has(orderItem.item.id)) continue

        uniqueItems.set(orderItem.item.id, true)
        const totalFulfilled = this.getTotalFulfilledForItem(orderItem.item.id)
        const totalRequired = this.order.orderItems
          .filter(oi => oi.item.id === orderItem.item.id)
          .reduce((sum, oi) => sum + this.getRequiredQuantity(oi), 0)

        if (totalFulfilled > totalRequired) {
          return true
        }
      }

      return false
    },

    validateFulfillmentItems(): string[] {
      const errors: string[] = []

      // Check that all order items are fully fulfilled
      const uniqueItems = new Map<string, boolean>()
      for (const orderItem of this.order.orderItems) {
        if (!orderItem.item.id || uniqueItems.has(orderItem.item.id)) continue
        
        uniqueItems.set(orderItem.item.id, true)
        const totalFulfilled = this.getTotalFulfilledForItem(orderItem.item.id)
        const totalRequired = this.order.orderItems
          .filter(oi => oi.item.id === orderItem.item.id)
          .reduce((sum, oi) => sum + this.getRequiredQuantity(oi), 0)

        if (totalFulfilled < totalRequired) {
          const remaining = totalRequired - totalFulfilled
          errors.push(this.t('itemNotFullyFulfilled', { 
            itemName: orderItem.item.name,
            remaining: remaining,
            fulfilled: totalFulfilled,
            required: totalRequired
          }))
        }
      }

      for (const fulfillmentItem of this.fulfillmentItems) {
        // Check batch number for expirable items
        if (fulfillmentItem.item.isExpirable) {
          if (!fulfillmentItem.extraProperties?.batchNumber) {
            errors.push(this.t('batchRequired', { itemName: fulfillmentItem.item.name }))
          }
        }

        // Check serial numbers for serialized items
        if (fulfillmentItem.item.isSerialized) {
          const requiredSerials = fulfillmentItem.quantity
          const selectedSerials = (fulfillmentItem.serialIDs || []).length

          if (selectedSerials < requiredSerials) {
            errors.push(this.t('serialsRequired', { 
              itemName: fulfillmentItem.item.name,
              required: requiredSerials,
              selected: selectedSerials
            }))
          }
        }
      }

      return errors
    },

    getOverfulfillmentDetails(): Array<{itemName: string, fulfilled: number, required: number, excess: number}> {
      const overfulfillments: Array<{itemName: string, fulfilled: number, required: number, excess: number}> = []
      const uniqueItems = new Map<string, boolean>()

      for (const orderItem of this.order.orderItems) {
        if (!orderItem.item.id || uniqueItems.has(orderItem.item.id)) continue

        uniqueItems.set(orderItem.item.id, true)
        const totalFulfilled = this.getTotalFulfilledForItem(orderItem.item.id)
        const totalRequired = this.order.orderItems
          .filter(oi => oi.item.id === orderItem.item.id)
          .reduce((sum, oi) => sum + this.getRequiredQuantity(oi), 0)

        if (totalFulfilled > totalRequired) {
          overfulfillments.push({
            itemName: orderItem.item.name,
            fulfilled: totalFulfilled,
            required: totalRequired,
            excess: totalFulfilled - totalRequired
          })
        }
      }

      return overfulfillments
    },

    isItemOverfulfilled(fulfillmentItem: OrderItem): boolean {
      if (!fulfillmentItem.item.id) return false

      const totalFulfilled = this.getTotalFulfilledForItem(fulfillmentItem.item.id)
      const totalRequired = this.order.orderItems
        .filter(oi => oi.item.id === fulfillmentItem.item.id)
        .reduce((sum, oi) => sum + this.getRequiredQuantity(oi), 0)

      return totalFulfilled > totalRequired
    },

    getOverfulfillmentMessage(fulfillmentItem: OrderItem): string {
      if (!fulfillmentItem.item.id) return ''

      const totalFulfilled = this.getTotalFulfilledForItem(fulfillmentItem.item.id)
      const totalRequired = this.order.orderItems
        .filter(oi => oi.item.id === fulfillmentItem.item.id)
        .reduce((sum, oi) => sum + this.getRequiredQuantity(oi), 0)

      const excess = totalFulfilled - totalRequired
      return excess > 0 ? (this.$ctx.locale == 'en' ? `Reduce by ${excess}`: `${excess}قلّل الكمية ب`) : ''
    },

    getTableRowClass({ row }: { row: OrderItem }): string {
      if (this.isItemOverfulfilled(row)) {
        return 'overfulfilled-row'
      }
      return ''
    },

    testDistributionScenario() {
      // Find an item that has multiple order lines for testing
      const itemsWithMultipleLines = new Map()
      this.order.orderItems.forEach(oi => {
        const itemId = oi.item.id
        if (!itemsWithMultipleLines.has(itemId)) {
          itemsWithMultipleLines.set(itemId, [])
        }
        itemsWithMultipleLines.get(itemId).push(oi)
      })

      for (const [, orderItems] of itemsWithMultipleLines) {
        if (orderItems.length > 1) {
          orderItems.forEach((oi: OrderItem, idx: number) => {
            const fulfilled = this.getFulfilledQuantity(oi)
            const required = this.getRequiredQuantity(oi)
          })
          break
        }
      }
    },

    removeFulfillmentItem(index: number) {
      this.fulfillmentItems.splice(index, 1)
    },

    onFulfillmentQuantityChange(fulfillmentItem: OrderItem, newQuantity: number) {
      // Calculate total required for this item across all order items
      const orderItems = this.order.orderItems.filter(oi => oi.item.id === fulfillmentItem.item.id)
      const totalRequired = orderItems.reduce((sum, oi) => sum + this.getRequiredQuantity(oi), 0)

      // Calculate total fulfilled by OTHER fulfillment items (excluding this one)
      const otherFulfilledTotal = this.fulfillmentItems
        .filter(fi => fi.item.id === fulfillmentItem.item.id && fi.selectID !== fulfillmentItem.selectID)
        .reduce((sum, fi) => sum + (fi.packInfo?.packQuantity ? fi.quantity * fi.packInfo.packQuantity : fi.quantity), 0)

      // Calculate the maximum this fulfillment item can have
      const maxAllowedForThis = Math.max(0, totalRequired - otherFulfilledTotal)

      // Adjust quantity to stay within limits
      const oldQuantity = fulfillmentItem.quantity
      const adjustedQuantity = Math.min(newQuantity, maxAllowedForThis)

      if (adjustedQuantity !== newQuantity) {
        ElMessage.warning(`${fulfillmentItem.item.name}: Quantity limited to ${adjustedQuantity} to prevent overfulfillment`)
      }

      fulfillmentItem.quantity = adjustedQuantity

      // Log current distribution status
      if (fulfillmentItem.item.id) {
        this.logDistributionStatus(fulfillmentItem.item)
      }
    },




    onSearchSelectPressedEnter() {
      this.pressedEnter = true
    },

    onSearchSelectKeyupEnter() {
      setTimeout(() => {
        this.pressedEnter = false
      }, 400)
    },

    getAvailableQuantity(item: Item) {
      if (item.unlimitedStock) {
        return this.t('unlimited')
      }

      let currentQuantity = item.currentLocationStock?.quantity || 0
      let pendingQuantity = item.currentLocationStock?.pendingQuantity || 0

      if (item.isExpirable && item.itemStocks) {
        currentQuantity = 0
        pendingQuantity = 0
        item.itemStocks.forEach((stock: any) => {
          currentQuantity += stock.quantity || 0
          pendingQuantity += stock.pendingQuantity || 0
        })
      }

      return currentQuantity - pendingQuantity
    },

    getAvailableSerials(fulfillmentItem: OrderItem) {
      // Return available (unused) serials for the item
      if (fulfillmentItem.item.itemStocks && fulfillmentItem.item.itemStocks.length > 0) {
        let serials = []

        // For expirable items, filter serials by selected batch
        if (fulfillmentItem.item.isExpirable && fulfillmentItem.extraProperties?.batchNumber) {
          const matchingStock = fulfillmentItem.item.itemStocks.find(
            (stock: any) =>
              stock.extraProperties?.batchNumber === fulfillmentItem.extraProperties.batchNumber
          )
          serials = matchingStock?.itemSerials || []
        } else if (!fulfillmentItem.item.isExpirable) {
          // For non-expirable items, return serials from first stock
          serials = fulfillmentItem.item.itemStocks[0].itemSerials || []
        }

        // Filter out used serials - only return available ones
        return serials.filter((serial: any) => !serial.used)
      }
      return []
    },

    getFulfilledQuantity(orderItem: OrderItem): number {
      // Get total fulfilled quantity for this item from the fulfillment pool
      const totalFulfilledForItem = this.getTotalFulfilledForItem(orderItem.item.id)

      // Find all order items for this item (sorted by their original order)
      const orderItems = this.order.orderItems.filter(oi => oi.item.id === orderItem.item.id)

      // Distribute fulfilled quantity across order items using FIFO (First In, First Out)
      let remainingFulfilled = totalFulfilledForItem
      let fulfilledForThisOrderItem = 0

      for (const oi of orderItems) {
        if (remainingFulfilled <= 0) break

        const required = this.getRequiredQuantity(oi)
        const canTakeFromPool = Math.min(remainingFulfilled, required)

        if (oi.selectID === orderItem.selectID) {
          fulfilledForThisOrderItem = canTakeFromPool
          break
        }

        remainingFulfilled -= canTakeFromPool
      }

      return fulfilledForThisOrderItem
    },

    getRequiredQuantity(orderItem: OrderItem): number {
      // For pack items, return quantity * pack quantity (total individual units)
      // For single items, return the order quantity
      return this.getDisplayQuantity(orderItem)
    },

    getFulfillmentStatusType(orderItem: OrderItem): string {
      const fulfilled = this.getFulfilledQuantity(orderItem)
      const required = this.getRequiredQuantity(orderItem)

      if (fulfilled === 0) return 'info'
      if (fulfilled >= required) return 'success'
      return 'warning'
    },

    getFulfillmentStatusText(orderItem: OrderItem): string {
      const fulfilled = this.getFulfilledQuantity(orderItem)
      const required = this.getRequiredQuantity(orderItem)

      if (fulfilled === 0) return this.t('pending')
      if (fulfilled >= required) return this.t('complete')
      return this.t('partial')
    },

    getTotalOrderedQuantity(): number {
      return this.order.orderItems.reduce((sum, item) => {
        return sum + this.getDisplayQuantity(item)
      }, 0)
    },

    getTotalScannedQuantity(): number {
      return this.fulfillmentItems.reduce((sum, item) => {
        // For pack items: quantity * pack quantity (total individual units)
        // For single items: just the quantity
        if (item.packInfo?.packQuantity) {
          return sum + (item.quantity * item.packInfo.packQuantity)
        }
        return sum + item.quantity
      }, 0)
    },

    getFulfillmentProgress(): number {
      const total = this.getTotalOrderedQuantity()
      const scanned = this.getTotalScannedQuantity()
      return total > 0 ? Math.round((scanned / total) * 100) : 0
    },

    openCashierPinDialog() {
      if (this.fulfillmentItems.length === 0) {
        ElMessage.warning('Please scan some items first')
        return
      }

      // Check for overfulfillment
      if (this.hasOverfulfillment()) {
        const overfulfillments = this.getOverfulfillmentDetails()
        const excessLabel = this.$ctx.locale == 'en' ? 'excess': 'زيادة';
        const overfulfillmentMessages = overfulfillments.map(item =>
          `${item.itemName}: ${item.fulfilled}/${item.required} (+${item.excess} ${excessLabel})`
        ).join('\n')

        this.$alertModal.showDanger({
          title: this.t('overfulfillmentError'),
          body: `${this.t('overfulfillmentWarning')}\n\n${overfulfillmentMessages}`
        })
        return
      }

      // Check for missing batch numbers and serials
      const validationErrors = this.validateFulfillmentItems()
      if (validationErrors.length > 0) {
        this.$alertModal.showDanger({
          title: this.t('fulfillmentIncomplete'),
          body: validationErrors.join('\n')
        })
        return
      }

      if (this.settingsStore.getValue('is_cashier_pin_required') == 'true') {
        this.cashierPinDialogOpen = true
        this.$nextTick(() => {
          setTimeout(() => {
            this.getCashierInputRef()?.focus()
          }, 0)
        })
        return
      }

      this.submitFulfillment()
    },

    async submitFulfillment() {
      if (this.settingsStore.getValue('is_cashier_pin_required') == 'true') {
        if (this.cashierPin === '') {
          this.$alertModal.showDanger({
            title: this.t('errCashierPinRequired')
          })
          return
        }
      }

      this.cashierPinDialogOpen = false

      try {
        const fulfillmentData = {
          orderId: this.order.id,
          fulfillmentItems: this.fulfillmentItems.map((item) => ({
            itemId: item.item.id,
            quantity: item.quantity,
            batchNumber: item.extraProperties?.batchNumber || null,
            serialIDs: item.serialIDs || [],
            packInfo: item.packInfo || null
          })),
          completedAt: new Date().toISOString(),
          completedBy: this.$ctx.currentUser?.fullName || 'Unknown'
        }

        const headers = this.cashierPin ? { pin: this.cashierPin } : {}
        await this.$http.post('sales/orders/fulfill', fulfillmentData, { headers })

        this.$alertModal.showSuccess({
          title: this.t('completeFulfillment'),
          body: this.t('fulfillmentCompleteSuccess')
        })

        // Navigate back to order
        setTimeout(() => {
          this.$router.push(this.$Route.SALES_ORDERS_ORDER.replace(':id', this.order.id as string))
        }, 1500)
      } catch (error: any) {
        this.$alertModal.showDanger({
          title: error.title || 'Error',
          body: error.body || 'Failed to complete fulfillment'
        })
      }

      // Clear PIN for security
      this.cashierPin = ''
    },

    cancelFulfillment() {
      this.$router.push(this.$Route.SALES_ORDERS_ORDER.replace(':id', this.order.id as string))
    },

    getCashierInputRef() {
      return this.$refs['cashierPinInput'] as any
    },

    getPackInfo(item: any): string {
      // Handle pack information safely
      if (item.pack) {
        return item.pack.name || item.pack.size || this.t('single')
      }
      return this.t('single')
    },

    getAddedCountClass(orderItem: OrderItem): string {
      const fulfilled = this.getFulfilledQuantity(orderItem)
      const required = this.getRequiredQuantity(orderItem)

      if (fulfilled === 0) return 'count-none'
      if (fulfilled >= required) return 'count-complete'
      return 'count-partial'
    },

    getProgressClass(orderItem: OrderItem): string {
      const fulfilled = this.getFulfilledQuantity(orderItem)
      const required = this.getRequiredQuantity(orderItem)

      if (fulfilled === 0) return 'progress-none'
      if (fulfilled >= required) return 'progress-complete'
      return 'progress-partial'
    },

    getOrderStatusClass(): string {
      const status = this.order.orderStatus?.toLowerCase() || ''

      if (status.includes('pending') || status.includes('new')) return 'status-pending'
      if (status.includes('processing') || status.includes('fulfilling')) return 'status-processing'
      if (status.includes('completed') || status.includes('fulfilled')) return 'status-completed'
      if (status.includes('cancelled') || status.includes('canceled')) return 'status-cancelled'

      return 'status-default'
    },

    getOrderStatusDisplay(): string {
      return this.order.orderStatus || this.t('unknown')
    },

    getOrderTypeClass(): string {
      const type = this.order.orderType?.toLowerCase() || ''

      if (type === 'direct') return 'type-direct'
      if (type === 'delivery') return 'type-delivery'
      if (type === 'pick_up') return 'type-pickup'

      return 'type-default'
    },

    formatOrderDate(): string {
      if (this.order.createdAt) {
        return new Date(this.order.createdAt).toLocaleDateString(this.$ctx.locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      return this.t('unknown')
    },

    getActionStatusClass(): string {
      return this.fulfillmentItems.length > 0 ? 'status-ready' : 'status-waiting'
    },

    getStatusBadgeClass(): string {
      const status = this.order.orderStatus?.toLowerCase() || ''

      if (status.includes('pending') || status.includes('new')) return 'badge-pending'
      if (status.includes('processing') || status.includes('fulfilling')) return 'badge-processing'
      if (status.includes('completed') || status.includes('fulfilled')) return 'badge-completed'
      if (status.includes('cancelled') || status.includes('canceled')) return 'badge-cancelled'

      return 'badge-default'
    },

    getFulfillmentIndicatorClass(orderItem: OrderItem): string {
      const fulfilled = this.getFulfilledQuantity(orderItem)
      const required = this.getRequiredQuantity(orderItem)

      if (fulfilled === 0) return 'indicator-none'
      if (fulfilled > required) return 'indicator-overfulfilled'
      if (fulfilled >= required) return 'indicator-complete'
      return 'indicator-partial'
    },

    getProgressStatusClass(): string {
      const progress = this.getFulfillmentProgress()

      if (progress === 0) return 'text-gray-500'
      if (progress === 100) return 'text-green-600'
      return 'text-blue-600'
    },

    getProgressStatusText(): string {
      const progress = this.getFulfillmentProgress()

      if (progress === 0) return this.t('notStarted')
      if (progress === 100) return this.t('completed')
      return this.t('inProgress')
    },

    getDineroValue(amount: number, currency: Currency, precision: number): string {
      const dinero = Dinero({
        amount,
        currency,
        precision
      })
      return formatDinero(dinero)
    },

    getItemTotal(orderItem: OrderItem): string {
      const total =
        (orderItem.quantity as number) *
        (orderItem.itemSalePriceAmount
          ? orderItem.itemSalePriceAmount
          : orderItem.item.salePriceAmount || 1)
      return this.getDineroValue(total, orderItem.currency || 'IQD', orderItem.precision || 3)
    },

    onPack(packInfo: any, oi: OrderItem): void {
      oi.packInfo = packInfo

      if (packInfo == null) {
        oi.itemSalePriceAmount = oi.item.salePriceAmount || 0
        return
      }

      oi.itemSalePriceAmount = packInfo.itemPack.salePriceAmount
    },

    openSerialDialog(oi: OrderItem): void {
      this.currentFulfillmentItem = oi
      this.serialDialogVisible = true
      this.$nextTick(() => {
        setTimeout(() => {
          this.getCashierInputRef()?.focus()
        }, 0)
      })
    },

    onBatchNumberChange(fulfillmentItem: OrderItem): void {
      // Handle batch number change and auto-populate expiry date
      if (fulfillmentItem.extraProperties?.batchNumber && fulfillmentItem.item.itemStocks) {
        // Find the stock with matching batch number and get its expiry date
        const matchingStock = fulfillmentItem.item.itemStocks.find(
          (stock: any) => stock.extraProperties?.batchNumber === fulfillmentItem.extraProperties?.batchNumber
        )

        if (matchingStock && matchingStock.extraProperties?.expiryDate) {
          // Note: We could add expiryDate to extraProperties if needed
          console.log('Found expiry date:', matchingStock.extraProperties.expiryDate)
        }
      }

      // For expirable serialized items, reset serial selection when batch changes
      if (fulfillmentItem.item.isExpirable && fulfillmentItem.item.isSerialized) {
        fulfillmentItem.serialIDs = []
        ElMessage.info({
          message: this.t('batchChangedResetSerials'),
          duration: 2000
        })
      }
    },

    getPackDisplayName(packInfo: any): string {
      if (!packInfo || !packInfo.itemPack) return ''
      
      // Follow NewCheckout logic for pack name display - use pack.enName/arName
      const pack = packInfo.itemPack.pack
      if (pack) {
        return this.$ctx.locale === 'en' ? pack.enName : pack.arName
      }
      
      // Fallback to itemPack name if pack object doesn't exist
      return packInfo.itemPack.name || packInfo.name || 'Pack'
    },

    getPackDisplayNameFromOrderItem(orderItem: OrderItem): string {
      // Get pack from the packs endpoint using packID
      if (orderItem.packInfo?.packID) {
        const pack = this.packMap.get(orderItem.packInfo.packID)
        if (pack) {
          return this.$ctx.locale === 'en' ? pack.enName : pack.arName
        }
      }
      return ''
    },

    isPackItem(orderItem: OrderItem): boolean {
      // It's a pack when packID is not null in packInfo
      return orderItem.packInfo?.packID != null
    },

    getDisplayQuantity(orderItem: OrderItem): number {
      // For pack items: quantity * pack quantity (total individual units)
      // For normal items: just the quantity
      if (this.isPackItem(orderItem)) {
        return orderItem.quantity * (orderItem.packInfo?.packQuantity || 1)
      }
      return orderItem.quantity
    },

    needsSerialsSelection(orderItem: OrderItem): boolean {
      // Check if this order item needs serial selection
      if (!orderItem.item.isSerialized) return false

      const requiredQuantity = this.getRequiredQuantity(orderItem)
      const fulfilledQuantityWithSerials = this.getFulfilledQuantityWithSerials(orderItem)

      // Yellow if not enough fulfilled quantity with serial numbers
      return fulfilledQuantityWithSerials < requiredQuantity
    },

    getFulfilledQuantityWithSerials(orderItem: OrderItem): number {
      // Get fulfilled quantity for this specific order item, but only count items with enough serials
      const allOrderItems = this.order.orderItems.filter(oi => oi.item.id === orderItem.item.id)
      const fulfillmentItemsWithSerials = this.fulfillmentItems.filter(fi => {
        if (fi.item.id !== orderItem.item.id) return false

        const requiredSerials = fi.quantity
        const selectedSerials = (fi.serialIDs || []).length
        return selectedSerials >= requiredSerials // Only items with enough serials
      })

      // Calculate total quantity from fulfillment items WITH enough serials
      let totalWithSerials = 0
      fulfillmentItemsWithSerials.forEach(fi => {
        if (fi.packInfo?.packQuantity) {
          totalWithSerials += (fi.quantity * fi.packInfo.packQuantity)
        } else {
          totalWithSerials += fi.quantity
        }
      })

      // Distribute the "with serials" quantity using FIFO logic
      let remainingWithSerials = totalWithSerials
      let fulfilledForThisOrderItemWithSerials = 0

      for (const oi of allOrderItems) {
        if (remainingWithSerials <= 0) break

        const required = this.getRequiredQuantity(oi)
        const canTakeFromSerialsPool = Math.min(remainingWithSerials, required)

        if (oi.selectID === orderItem.selectID) {
          fulfilledForThisOrderItemWithSerials = canTakeFromSerialsPool
          break
        }

        remainingWithSerials -= canTakeFromSerialsPool
      }

      return fulfilledForThisOrderItemWithSerials
    },

    needsBatchSelection(orderItem: OrderItem): boolean {
      // Check if this order item needs batch selection
      if (!orderItem.item.isExpirable) return false

      const requiredQuantity = this.getRequiredQuantity(orderItem)
      const fulfilledQuantityWithBatch = this.getFulfilledQuantityWithBatch(orderItem)

      // Yellow if not enough fulfilled quantity with batch numbers
      return fulfilledQuantityWithBatch < requiredQuantity
    },

    getFulfilledQuantityWithBatch(orderItem: OrderItem): number {
      // Get fulfilled quantity for this specific order item, but only count items with batch numbers
      const totalFulfilledForItem = this.getTotalFulfilledForItem(orderItem.item.id)
      const allOrderItems = this.order.orderItems.filter(oi => oi.item.id === orderItem.item.id)
      const fulfillmentItemsWithBatch = this.fulfillmentItems.filter(fi =>
        fi.item.id === orderItem.item.id && fi.extraProperties?.batchNumber
      )

      // Calculate total quantity from fulfillment items WITH batch numbers
      let totalWithBatch = 0
      fulfillmentItemsWithBatch.forEach(fi => {
        if (fi.packInfo?.packQuantity) {
          totalWithBatch += (fi.quantity * fi.packInfo.packQuantity)
        } else {
          totalWithBatch += fi.quantity
        }
      })

      // Distribute the "with batch" quantity using FIFO logic
      let remainingWithBatch = totalWithBatch
      let fulfilledForThisOrderItemWithBatch = 0

      for (const oi of allOrderItems) {
        if (remainingWithBatch <= 0) break

        const required = this.getRequiredQuantity(oi)
        const canTakeFromBatchPool = Math.min(remainingWithBatch, required)

        if (oi.selectID === orderItem.selectID) {
          fulfilledForThisOrderItemWithBatch = canTakeFromBatchPool
          break
        }

        remainingWithBatch -= canTakeFromBatchPool
      }

      return fulfilledForThisOrderItemWithBatch
    },

    getSerialSelectionStatus(orderItem: OrderItem): { required: number, selected: number } {
      // Get serial selection status for an order item
      const fulfillmentItems = this.fulfillmentItems.filter(fi => fi.item.id === orderItem.item.id)
      
      let totalRequired = 0
      let totalSelected = 0
      
      fulfillmentItems.forEach(fi => {
        totalRequired += fi.quantity
        totalSelected += (fi.serialIDs || []).length
      })
      
      return { required: totalRequired, selected: totalSelected }
    },

    onPackFulfillment(packInfo: any, fulfillmentItem: OrderItem): void {
      // Handle pack selection for fulfillment items
      fulfillmentItem.packInfo = packInfo

      if (packInfo == null) {
        // Reset to single item pricing
        fulfillmentItem.itemSalePriceAmount = fulfillmentItem.item.salePriceAmount || 0
        return
      }

      // Set pack pricing and info
      fulfillmentItem.itemSalePriceAmount = packInfo.itemPack.salePriceAmount || packInfo.salePriceAmount || 0
    }
  }
})
</script>

<style>
@import url(../../assets/css/index.css);

.bg-primary {
  background-color: var(--primary-bg-color);
}

.border-primary {
  border: 1px solid var(--primary-bg-color);
}

.text-primary {
  color: var(--primary-bg-color);
}

.bg-light-blue {
  background-color: #f2f7ff;
}

.items-table {
  height: 65vh !important;
}

form > h1 {
  @apply p-2 text-lg py-3 font-bold;
}

form > h2 {
  @apply p-2 py-3 font-bold;
}

form > h3 {
  @apply p-2 py-3 font-bold;
}

.card {
  @apply shadow-sm border border-gray-200;
}

.my-header {
  @apply bg-gray-50 text-gray-700 font-medium;
}

/* Right Panel Styling */
.order-info-card {
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.order-info-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
}

.badge-pending {
  background: #fef3c7;
  color: #92400e;
}

.badge-processing {
  background: #dbeafe;
  color: #1e40af;
}

.badge-completed {
  background: #d1fae5;
  color: #065f46;
}

.badge-cancelled {
  background: #fee2e2;
  color: #991b1b;
}

.badge-default {
  background: #f1f5f9;
  color: #475569;
}

.items-quick-list {
  border: 1px solid #e5e7eb;
}

.item-summary-row:hover {
  background-color: #f9fafb;
}

.fulfillment-indicator {
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  min-width: 35px;
}

.indicator-none {
  background: #f3f4f6;
  color: #6b7280;
}

.indicator-partial {
  background: #fef3c7;
  color: #92400e;
}

.indicator-complete {
  background: #d1fae5;
  color: #065f46;
}

.indicator-overfulfilled {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #f87171;
  animation: pulse-warning 2s infinite;
}

.excess-indicator {
  color: #dc2626;
  font-weight: bold;
  font-size: 10px;
  background: #fef2f2;
  padding: 1px 3px;
  border-radius: 3px;
  margin-left: 2px;
  border: 1px solid #fca5a5;
  display: inline-block;
}

@keyframes pulse-warning {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.summary-card {
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.summary-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.stat-item {
  padding: 8px;
}

.progress-bar-bg {
  background: #e5e7eb;
}

.progress-bar-fill {
  background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 9999px;
}

.action-section {
  margin-top: auto;
}

.item-serial {
  height: 20px;
  width: 65px;
  cursor: pointer;
}

.checkout-quantity-el-input-number {
  width: 115px;
}

/* Overfulfillment row styling */
.overfulfilled-row {
  background-color: #fef2f2 !important;
  border-left: 4px solid #ef4444 !important;
}

.overfulfilled-row:hover {
  background-color: #fde8e8 !important;
}

.overfulfillment-note {
  font-size: 10px;
  line-height: 1.2;
}

.overfulfilled-warning {
  animation: pulse-warning 2s infinite;
}

.overfulfilled-input {
  border: 2px solid #ef4444 !important;
  background-color: #fef2f2 !important;
}

.overfulfilled-input:focus {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2) !important;
}
</style>
